import type { Language } from '../types'
import { VoiceInputError, type VoiceInputProvider } from './VoiceInputProvider'

export interface NamedVoiceInputProvider {
  id: 'whisper' | 'web_speech' | 'unspecified'
  provider: VoiceInputProvider
}

export interface VoiceProviderAttempt {
  provider: NamedVoiceInputProvider['id']
  outcome: 'unavailable' | 'success' | 'failure'
  durationMs: number
  errorCode?: string
}

type VoiceProviderEntry = VoiceInputProvider | NamedVoiceInputProvider
type AttemptListener = (attempt: VoiceProviderAttempt) => void

function namedProvider(entry: VoiceProviderEntry): NamedVoiceInputProvider {
  return 'provider' in entry ? entry : { id: 'unspecified', provider: entry }
}

export class FallbackVoiceInputProvider implements VoiceInputProvider {
  constructor(
    private readonly providers: readonly VoiceProviderEntry[],
    private readonly onAttempt?: AttemptListener,
  ) {}

  get available(): boolean {
    return this.providers.some((entry) => namedProvider(entry).provider.available)
  }

  async listen(language: Language): Promise<string> {
    let lastError: unknown = new VoiceInputError('UNSUPPORTED')

    for (const entry of this.providers) {
      const { id, provider } = namedProvider(entry)
      if (!provider.available) {
        this.emit({ provider: id, outcome: 'unavailable', durationMs: 0, errorCode: 'UNSUPPORTED' })
        continue
      }
      const startedAt = Date.now()
      try {
        const transcript = await provider.listen(language)
        this.emit({ provider: id, outcome: 'success', durationMs: Date.now() - startedAt })
        return transcript
      } catch (error) {
        this.emit({
          provider: id,
          outcome: 'failure',
          durationMs: Date.now() - startedAt,
          errorCode: error instanceof VoiceInputError ? (error.upstreamCode ?? error.code) : 'UNKNOWN',
        })
        // Only an unsupported provider may fall through to the next implementation.
        // Once a provider has attempted microphone capture or transcription, trying
        // Web Speech in the same interaction can trigger a second permission prompt.
        if (!(error instanceof VoiceInputError) || error.code !== 'UNSUPPORTED') throw error
        lastError = error
      }
    }

    if (lastError instanceof VoiceInputError) throw lastError
    throw new VoiceInputError('RECOGNITION_FAILED')
  }

  private emit(attempt: VoiceProviderAttempt): void {
    try {
      this.onAttempt?.(attempt)
    } catch {
      // Telemetry must never affect the voice fallback path.
    }
  }
}
